import { useState } from "react";
import { createRoute } from "@tanstack/react-router";
import { facilitateRoute } from "./facilitate";
import {
  useAcceptFacilitatorInviteMutation,
  useDeclineFacilitatorInviteMutation,
  useMyFacilitatorInvitationQuery,
} from "@/lib/facilitator-invite";
import { NotFound } from "@/components/NotFound";
import styles from "./facilitate-invite.module.scss";

function FacilitateInvite() {
  const query = useMyFacilitatorInvitationQuery();
  const accept = useAcceptFacilitatorInviteMutation();
  const decline = useDeclineFacilitatorInviteMutation();
  const [error, setError] = useState<string | null>(null);

  if (query.isLoading) return <Loading />;

  const row = query.data;
  if (!row) return <NotFound />;

  const inviterName =
    row.invitedBy?.displayName || row.invitedBy?.email || "An operator";

  async function handleAccept() {
    setError(null);
    try {
      await accept.mutateAsync();
    } catch (err) {
      setError((err as Error)?.message ?? "Failed to accept invitation");
    }
  }

  async function handleDecline() {
    setError(null);
    try {
      await decline.mutateAsync();
    } catch (err) {
      setError((err as Error)?.message ?? "Failed to decline invitation");
    }
  }

  const pending = accept.isPending || decline.isPending;

  return (
    <main className={styles.shell}>
      <h1 className={styles.title}>Facilitator invitation</h1>
      <p className={styles.subtitle}>
        Facilitators add warmth to AI-guided reading rooms by hosting alongside
        Margaux. You can accept or decline this invitation at any time.
      </p>

      <section className={styles.card}>
        <h2 className={styles.cardTitle}>
          {inviterName} invited you to facilitate
        </h2>
        <p className={styles.cardMeta}>
          Invited{" "}
          {new Date(row.invitedAt).toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
          })}
        </p>

        {row.status === "pending" ? (
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.acceptBtn}
              onClick={handleAccept}
              disabled={pending}
            >
              {accept.isPending ? "Accepting…" : "Accept"}
            </button>
            <button
              type="button"
              className={styles.declineBtn}
              onClick={handleDecline}
              disabled={pending}
            >
              {decline.isPending ? "Declining…" : "Decline"}
            </button>
          </div>
        ) : null}

        {row.status === "active" ? (
          <p className={styles.statusNote}>
            You've accepted this invitation — you're now a facilitator.
          </p>
        ) : null}

        {row.status === "declined" ? (
          <p className={styles.statusNote}>
            You've declined this invitation. Reach out to the operator if you
            change your mind.
          </p>
        ) : null}

        {row.status === "revoked" ? (
          <p className={styles.statusNote}>
            Your facilitator access has been revoked.
            {row.revokedReason ? ` Reason: ${row.revokedReason}` : ""}
          </p>
        ) : null}

        {error ? (
          <div className={styles.errorBanner} role="alert">
            {error}
          </div>
        ) : null}
      </section>
    </main>
  );
}

function Loading() {
  return (
    <div role="status" aria-live="polite">
      Loading…
    </div>
  );
}

export const facilitateInviteRoute = createRoute({
  getParentRoute: () => facilitateRoute,
  path: "/invite",
  component: FacilitateInvite,
});
